// Demo ledger data ported verbatim from the "Gallery · Field" reference designs
// (VPN/Insurance/Transport/Tours/Stay compare tables). These pages have no
// aggregated Sanity feed yet, so the figures mirror the mockups — swap for a
// real feed when one exists. Each builder returns a serializable config for
// <GfServiceLedger />.

import { TOUR_CATEGORY_ICONS, DEFAULT_TOUR_ICON } from '@/components/tours/TourCard'
import { t } from '@/i18n/translations'

const opList = (entries) => entries.map(([id, name]) => ({ id, name }))

/* ---------------- VPN — provider switcher · monthly/yearly ---------------- */
export function vpnLedgerConfig(lang = 'ru') {
    const ui = t(lang, 'ledgerLabels')
    const NAMES = { nord: 'NordVPN', surf: 'Surfshark', express: 'ExpressVPN', proton: 'Proton VPN' }
    // [plan, devices(999=∞), monthly, yearlyPerMonth, rec]
    const PLANS = {
        nord: [['Basic', 10, 12.99, 3.39, 1], ['Plus', 10, 13.99, 4.39, 0], ['Complete', 10, 14.99, 5.99, 0]],
        surf: [['Starter', 999, 15.45, 2.19, 1], ['One', 999, 17.95, 2.99, 0], ['One+', 999, 21.95, 4.49, 0]],
        express: [['Standard', 8, 12.95, 5.99, 0]],
        proton: [['Plus', 10, 9.99, 4.49, 1], ['Unlimited', 10, 11.99, 5.99, 0]],
    }
    const build = (priceIdx) => Object.fromEntries(Object.entries(PLANS).map(([id, rows]) => [
        id, rows.map((r) => ({ plan: r[0], devices: r[1], price: r[priceIdx], rec: !!r[4], opName: NAMES[id] })),
    ]))
    return {
        ops: opList([['all', ui.all.services], ['nord', 'NordVPN'], ['surf', 'Surfshark'], ['express', 'ExpressVPN'], ['proton', 'Proton VPN']]),
        data: { local: build(2), regional: build(3) },
        columns: [
            { key: 'plan', label: ui.col.plan, fmt: 'text', sortable: true, opTag: true, rec: true },
            { key: 'devices', label: ui.col.devices, fmt: 'devices', sortable: true },
            { key: 'price', label: ui.col.pricePerMo, fmt: 'price', sortable: true },
        ],
        scope: { local: ui.scope.monthly, regional: ui.scope.yearly },
        note: {
            local: ui.note.vpnLocal,
            regional: ui.note.vpnRegional,
        },
        gridClass: 'gf-plans-c3', defaultSort: 'price',
        selectLabel: ui.select.vpn, selectHref: '#providers',
    }
}

/* ---------------- Insurance — insurer switcher · region scope ---------------- */
export function insuranceLedgerConfig(lang = 'ru', countryName = '') {
    const ui = t(lang, 'ledgerLabels')
    const NAMES = { chere: 'Черехапа', trip: 'Tripinsurance', ekta: 'Ekta', polis: 'Polis812' }
    // [plan, coverage(€k), localPerDay, regionalPerDay, rec]
    const PLANS = {
        chere: [['Базовый', 30, 0.80, 1.10, 1], ['Стандарт', 50, 1.10, 1.50, 0], ['Премиум', 100, 1.80, 2.40, 0]],
        trip: [['Лайт', 30, 1.10, 1.40, 0], ['Оптимум', 50, 1.50, 1.90, 1], ['Макси', 100, 2.30, 2.90, 0]],
        ekta: [['Standard', 40, 1.30, 1.60, 0], ['Comfort', 75, 1.90, 2.30, 0]],
        polis: [['Эконом', 30, 1.00, 1.30, 0], ['Виза', 50, 1.40, 1.70, 1]],
    }
    const build = (priceIdx) => Object.fromEntries(Object.entries(PLANS).map(([id, rows]) => [
        id, rows.map((r) => ({ plan: r[0], coverage: r[1], price: r[priceIdx], rec: !!r[4], opName: NAMES[id] })),
    ]))
    return {
        ops: opList([['all', ui.all.services], ['chere', 'Черехапа'], ['trip', 'Tripinsurance'], ['ekta', 'Ekta'], ['polis', 'Polis812']]),
        data: { local: build(2), regional: build(3) },
        columns: [
            { key: 'plan', label: ui.col.plan, fmt: 'text', sortable: true, opTag: true, rec: true },
            { key: 'coverage', label: ui.col.coverage, fmt: 'coverage', sortable: true },
            { key: 'price', label: ui.col.pricePerDay, fmt: 'price', sortable: true },
        ],
        scope: { local: countryName || ui.scope.country, regional: ui.scope.schengen },
        note: {
            local: ui.note.insuranceLocal(countryName),
            regional: ui.note.insuranceRegional,
        },
        gridClass: 'gf-plans-c3', defaultSort: 'price',
        selectLabel: ui.select.insurance, selectHref: '#providers',
    }
}

/* ---------------- Transport — mode switcher · domestic/cross-border ---------------- */
export function transportLedgerConfig(lang = 'ru', countryName = '') {
    const ui = t(lang, 'ledgerLabels')
    const NAMES = ui.mode
    const perDay = ui.perDay
    // route, minutes (0 = per-day rental), price, rec, customTime
    const LOCAL = {
        bus: [['Будва → Котор', 30, 4, 0], ['Котор → Херцег-Нови', 50, 5, 0], ['Будва → Бар', 60, 6, 0], ['Подгорица → Будва', 80, 7, 0], ['Подгорица → Жабляк', 180, 9, 0]],
        transfer: [['Аэропорт Тиват → Котор', 35, 25, 1], ['Аэропорт Тиват → Будва', 30, 30, 0], ['Котор → Будва', 30, 28, 0], ['Аэропорт Подгорица → Будва', 60, 45, 0]],
        ferry: [['Каменари → Лепетане', 10, 4.5, 0], ['Котор → Пераст (катер)', 30, 15, 0]],
        car: [['Эконом · хетчбэк', 0, 18, 0, perDay], ['Кроссовер · SUV', 0, 32, 1, perDay], ['Минивэн · 7 мест', 0, 45, 0, perDay]],
    }
    const REGIONAL = {
        bus: [['Будва → Дубровник', 150, 20, 0], ['Подгорица → Тирана', 180, 22, 0], ['Котор → Сараево', 360, 30, 0], ['Будва → Белград', 540, 35, 0]],
        transfer: [['Аэропорт Дубровник → Котор', 90, 60, 1], ['Тиват → Дубровник', 90, 70, 0]],
        ferry: [['Бар → Бари (Италия)', 540, 55, 0], ['Бар → Анкона (Италия)', 960, 70, 0]],
        car: [['Эконом · выезд за границу', 0, 24, 0, perDay], ['Кроссовер · выезд за границу', 0, 40, 0, perDay]],
    }
    const conv = (tree) => Object.fromEntries(Object.entries(tree).map(([id, rows]) => [
        id, rows.map((r) => ({ route: r[0], minutes: r[1], price: r[2], rec: !!r[3], _timeLabel: r[4] || null, opName: NAMES[id] })),
    ]))
    return {
        ops: opList([['all', ui.all.modes], ['bus', NAMES.bus], ['transfer', NAMES.transfer], ['ferry', NAMES.ferry], ['car', NAMES.car]]),
        data: { local: conv(LOCAL), regional: conv(REGIONAL) },
        columns: [
            { key: 'route', label: ui.col.route, fmt: 'text', sortable: true, opTag: true, rec: true },
            { key: 'minutes', label: ui.col.time, fmt: 'time', sortable: true },
            { key: 'price', label: ui.col.price, fmt: 'price', sortable: true },
        ],
        scope: { local: ui.scope.domestic, regional: ui.scope.crossBorder },
        note: {
            local: ui.note.transportLocal(countryName),
            regional: ui.note.transportRegional,
        },
        gridClass: 'gf-plans-c3', defaultSort: 'price',
        selectLabel: ui.select.transport, selectHref: '#providers',
    }
}

/* ---------------- Tours — filters · group/private ---------------- */
// Fed by REAL Sanity data when the country has imported `tour` documents
// (countryToursLedgerQuery → toursPage.js): rows are grouped by primary
// category, split into group/private scopes, top N per category. Falls back
// to the demo mockup figures for countries without a tours feed.
// Canonical category keys (ordering + membership) — labels live in ledgerLabels.cat
const TOUR_CAT_KEYS = ['sightseeing', 'nature', 'culture', 'history', 'food', 'adventure', 'boat_tours', 'other']
const tourGroupBucket = (groupType) => (groupType === 'private' ? 'private' : 'group')
// Длительность → бакет для селекта-фильтра
const durBucket = (minutes) => {
    if (typeof minutes !== 'number') return 'flex'
    if (minutes < 180) return 'short'
    if (minutes <= 360) return 'half'
    return 'long'
}

const SRC_NAMES = { tripster: 'Tripster', viator: 'Viator' }

function buildToursLedgerData(tours, ui) {
    const toRow = (t, cat) => {
        const sourceName = SRC_NAMES[t.source] || t.source
        return {
            tour: t.title,
            city: t.cityName || '—',
            // 9999 — чтобы туры без фиксированной длительности падали в конец сортировки
            minutes: typeof t.durationMinutes === 'number' ? t.durationMinutes : 9999,
            // «по программе» — у части туров длительность не фиксирована (varies)
            _timeLabel: typeof t.durationMinutes === 'number' ? null : ui.flexible,
            price: typeof t.priceFrom === 'number' ? t.priceFrom : 0,
            _cur: t.currency === 'USD' ? '$' : '€',
            _cat: cat,
            _src: t.source,
            _groupType: tourGroupBucket(t.groupType),
            groupType: ui.groupType[tourGroupBucket(t.groupType)],
            _dur: durBucket(t.durationMinutes),
            _langs: Array.isArray(t.languages) ? t.languages : [],
            _kids: t.childFriendly === true,
            _instant: t.instantBooking === true,
            opName: ui.cat[cat],
            _icon: TOUR_CATEGORY_ICONS[cat] || DEFAULT_TOUR_ICON,
            rec: !!t.featured,
            selHref: t.url,
            _ext: true,
            // Подпись кнопки последнего столбца — по площадке тура
            _selLabel: ui.selSource(sourceName),
        }
    }
    const grouped = {}
    for (const t of tours) {
        const cat = TOUR_CAT_KEYS.includes((t.categories || [])[0]) ? t.categories[0] : 'other'
        const bucket = grouped
        if (!bucket[cat]) bucket[cat] = []
        // вход уже отсортирован запросом (featured → источник → язык → рейтинг)
        bucket[cat].push(toRow(t, cat))
    }
    return grouped
}

export function toursLedgerConfig(lang = 'ru', tours = []) {
    const isRu = lang === 'ru'
    const ui = t(lang, 'ledgerLabels')

    if (Array.isArray(tours) && tours.length > 0) {
        const grouped = buildToursLedgerData(tours, ui)
        const cats = TOUR_CAT_KEYS.filter((c) => grouped[c])

        // Опции фильтров — по реально встречающимся значениям
        const cities = [...new Set(tours.map((t) => t.cityName || '—'))].sort()
        const sources = [...new Set(tours.map((t) => t.source))]
        const groupTypes = [...new Set(tours.map((t) => tourGroupBucket(t.groupType)))]
        const durs = [...new Set(tours.map((t) => durBucket(t.durationMinutes)))]
        // Языки — мультивыбор на обоих языках сайта (выбрать можно несколько)
        const langsPresent = [...new Set(tours.flatMap((t) => t.languages || []))]
        const langOptions = langsPresent
            .map((l) => ({ value: l, label: ui.tourLang[l] || l.toUpperCase() }))
            .sort((a, b) => a.label.localeCompare(b.label))
        const hasKids = tours.some((t) => t.childFriendly === true)
        const hasInstant = tours.some((t) => t.instantBooking === true)

        const filters = [
            groupTypes.length > 1 && {
                key: '_groupType',
                type: 'multi',
                label: ui.filter.tourType,
                allLabel: ui.filter.tourTypeAll,
                options: ['group', 'private']
                    .filter((value) => groupTypes.includes(value))
                    .map((value) => ({ value, label: ui.groupType[value] })),
            },
            // Категории — бейджи-чипы с иконками (мультивыбор)
            cats.length > 1 && {
                key: '_cat',
                type: 'chips',
                label: ui.filter.categories,
                pinned: true,
                options: cats.map((c) => ({
                    value: c,
                    label: ui.cat[c],
                    icon: TOUR_CATEGORY_ICONS[c] || DEFAULT_TOUR_ICON,
                })),
            },
            cities.length > 1 && {
                key: 'city',
                type: 'select',
                label: ui.filter.city,
                allLabel: ui.filter.cityAll,
                options: cities.map((c) => ({ value: c, label: c })),
            },
            langOptions.length > 1 && {
                key: '_langs',
                type: 'multi',
                label: ui.filter.language,
                allLabel: ui.filter.languageAll,
                options: langOptions,
            },
            durs.length > 1 && {
                key: '_dur',
                type: 'select',
                label: ui.filter.length,
                allLabel: ui.filter.lengthAll,
                options: ['short', 'half', 'long', 'flex'].filter((d) => durs.includes(d))
                    .map((d) => ({ value: d, label: ui.dur[d] })),
            },
            sources.length > 1 && {
                key: '_src',
                type: 'select',
                label: ui.filter.platform,
                allLabel: ui.filter.platformAll,
                options: sources.map((s) => ({ value: s, label: SRC_NAMES[s] || s })),
            },
            hasKids && { key: '_kids', type: 'toggle', label: ui.filter.kids },
            hasInstant && { key: '_instant', type: 'toggle', label: ui.filter.instant },
        ].filter(Boolean)

        return {
            data: { local: grouped },
            columns: [
                { key: 'tour', label: ui.col.tour, fmt: 'text', sortable: true, opTag: true, opTagFilterKey: '_cat', rec: true },
                { key: 'city', label: ui.col.city, fmt: 'text', sortable: true, filterKey: 'city' },
                { key: 'groupType', label: ui.col.type, fmt: 'text', sortable: true, filterKey: '_groupType', filterValueKey: '_groupType' },
                { key: 'minutes', label: ui.col.length, fmt: 'time', sortable: true },
                { key: 'price', label: ui.col.price, fmt: 'priceInt', sortable: true },
            ],
            note: {
                local: ui.note.toursLocal,
                regional: ui.note.toursRegional,
            },
            filters,
            collapsibleFilters: true,
            initialLimit: 8,
            // RU-посетителям приоритезируем туры Tripster (русскоязычная площадка)
            priorityKey: isRu ? '_src' : null,
            priorityValues: isRu ? ['tripster'] : [],
            gridClass: 'gf-plans-tours', defaultSort: 'price',
            selectLabel: ui.select.tours, selectHref: '#providers',
        }
    }

    // ── Demo fallback (страны без выгрузки туров) ──
    const NAMES = ui.catDemo
    // tour, city, hours, groupPrice, privatePrice, rec
    const TOURS = {
        boat: [['Бухта Котора на катере', 'Котор', 3, 25, 90, 1], ['Закат на яхте', 'Будва', 2, 35, 120, 0], ['Острова Перастa', 'Пераст', 2, 20, 75, 0]],
        nature: [['Скадарское озеро', 'Вирпазар', 6, 40, 150, 1], ['Каньон Тары и Дурмитор', 'Жабляк', 10, 55, 210, 0], ['Биоградска гора', 'Колашин', 7, 45, 165, 0]],
        history: [['Старый Котор пешком', 'Котор', 2, 18, 70, 0], ['Цетине и Негуши', 'Цетине', 5, 45, 160, 1]],
        food: [['Вино и пршут Негушей', 'Негуши', 4, 38, 130, 1], ['Рыбный обед в Перасте', 'Пераст', 3, 30, 110, 0]],
    }
    const build = () => Object.fromEntries(Object.entries(TOURS).map(([id, rows]) => [
        id, rows.flatMap((r) => ([
            { tour: r[0], city: r[1], groupType: ui.groupType.group, hours: r[2], price: r[3], _groupType: 'group', rec: !!r[5], opName: NAMES[id] },
            { tour: r[0], city: r[1], groupType: ui.groupType.private, hours: r[2], price: r[4], _groupType: 'private', rec: !!r[5], opName: NAMES[id] },
        ])),
    ]))
    return {
        ops: opList([['all', ui.all.types], ['boat', NAMES.boat], ['nature', NAMES.nature], ['history', NAMES.history], ['food', NAMES.food]]),
        data: { local: build() },
        columns: [
            { key: 'tour', label: ui.col.tour, fmt: 'text', sortable: true, opTag: true, rec: true },
            { key: 'city', label: ui.col.city, fmt: 'text', sortable: true },
            { key: 'groupType', label: ui.col.type, fmt: 'text', sortable: true, filterKey: '_groupType', filterValueKey: '_groupType' },
            { key: 'hours', label: ui.col.length, fmt: 'duration', sortable: true },
            { key: 'price', label: ui.col.price, fmt: 'priceInt', sortable: true },
        ],
        note: {
            local: ui.note.toursDemoLocal,
            regional: ui.note.toursDemoRegional,
        },
        filters: [{
            key: '_groupType',
            type: 'multi',
            label: ui.filter.tourType,
            allLabel: ui.filter.tourTypeAll,
            options: ['group', 'private'].map((value) => ({ value, label: ui.groupType[value] })),
        }],
        initialLimit: 8,
        gridClass: 'gf-plans-tours', defaultSort: 'price',
        selectLabel: ui.select.tours, selectHref: '#providers',
    }
}

/* ---------------- Stay — average price level per city ---------------- */
// Unlike the demo ledgers above, this one is fed by REAL Sanity data: the page
// computes the average accommodation price tier ($ / $$ / $$$ / $$$$) per city
// from the hotels feed and passes one row per city. See stayPage.js.
export function stayLedgerConfig(lang = 'ru', cityRows = []) {
    const ui = t(lang, 'ledgerLabels')
    return {
        ops: [],
        data: { local: { cities: cityRows } },
        columns: [
            { key: 'name', label: ui.col.cityStay, fmt: 'text', sortable: true, rec: true },
            { key: 'count', label: ui.col.options, fmt: 'count', sortable: true },
            { key: 'tier', label: ui.col.priceLevel, fmt: 'tier', sortable: true },
        ],
        scope: null,
        note: {},
        gridClass: 'gf-plans-c3', defaultSort: 'tier',
        selectLabel: ui.select.stay, selectHref: '#providers',
    }
}
